import type { DeleteRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useChangeRequestConfirm } from 'src/providers/change-request-confirm-context';
import { useTRPC } from 'src/utils/trpc';

import { withChangeRequestConfirm } from '../_with-change-request-confirm';
import { invalidateChangeRequestFeeds } from '../invalidateChangeRequestFeeds';

const mapTrpcResponseToGraphQL = (): DeleteRiskMutation => {
  return {
    deleteRiskById: {
      __typename: 'GenericMutationOutput',
      // trpc throws on 0 affected rows; on the change-request branch no row
      // is deleted yet (a CR was raised) but the action still "succeeded"
      // from the caller's perspective, mirroring the legacy delete flow.
      affected_rows: 1,
    },
  };
};

export const useDeleteRiskTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const changeRequestConfirm = useChangeRequestConfirm();
  const mutation = useMutation({
    ...trpc.frontend.risk.delete.mutationOptions({
      onSuccess: async (result) => {
        // RSP-5789 — when the mutation was gated into a change request
        // (created or amended), surface it on the CR read surfaces
        // immediately instead of waiting for the panel's next poll.
        if (result.status !== 'applied') {
          await invalidateChangeRequestFeeds(queryClient, trpc);
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.riskById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.scores.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.riskListOnlyOptimized.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.risk.riskListOnlyWithEntitiesOptimized.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.riskScoresByRiskId.queryKey(),
        });
      },
    }),
  });

  return {
    deleteRisk: async (id: string): Promise<DeleteRiskMutation> => {
      // risk.delete is single-gated through runWithApprovalWorkflow: it throws
      // CHANGE_REQUEST_REQUIRED when approval is needed (the other entity
      // deletes are bulk and return the partition directly). Without the
      // confirm wrapper the modal never opens and the delete just surfaces a
      // "Failed to save changes" toast. withChangeRequestConfirm surfaces the
      // modal and replays with _confirmChangeRequest.
      //
      // A confirmed delete returns `change-request-created` (a CR was raised),
      // not `applied`. All three success statuses are fine here; only a thrown
      // error (the user cancelling the modal, or a real failure) is
      // non-success, and withChangeRequestConfirm re-throws those. (The update
      // hooks follow the same rule via an inline `result.status === 'applied'`
      // branch — neither path may throw on the CR-created result.)
      await withChangeRequestConfirm({
        input: { id },
        run: (variables) => mutation.mutateAsync(variables),
        confirm: changeRequestConfirm,
      });

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
